import React, { useState } from "react";
import { CloudHail, ShieldAlert, Paintbrush, ParkingCircle, ChevronDown } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const ICONS = {
  hagel: CloudHail,
  steinschlag: ShieldAlert,
  lack: Paintbrush,
  parkschaden: ParkingCircle,
};

function SpectrumCard({ item, index, expanded, onToggle }) {
  const Icon = ICONS[item.key] || ShieldAlert;
  return (
    <article
      data-testid={`spectrum-card-${item.key}`}
      className={`group relative bg-[#0e0e0e] border rounded-xl overflow-hidden transition-all duration-500 ${
        expanded ? "border-[#e52620]/40 md:col-span-2" : "border-white/10 hover:border-white/30"
      }`}
    >
      <button
        onClick={onToggle}
        data-testid={`spectrum-toggle-${item.key}`}
        className="w-full text-left p-7 md:p-8 flex items-start gap-5"
      >
        <div className="w-12 h-12 rounded-md bg-[#1a1a1a] border border-white/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-[#e52620]" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono font-bold">
            0{index + 1}
          </div>
          <h3 className="mt-1 text-2xl md:text-3xl font-bold text-white tracking-tight">
            {item.title}
          </h3>
          <p className="mt-2 text-zinc-400 leading-relaxed">{item.short}</p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-zinc-400 transition-transform duration-300 flex-shrink-0 ${
            expanded ? "rotate-180 text-[#e52620]" : ""
          }`}
        />
      </button>

      {expanded && (
        <div
          data-testid={`spectrum-details-${item.key}`}
          className="px-7 md:px-8 pb-8 border-t border-white/5 pt-6 space-y-5"
        >
          {item.details.map((d, i) => (
            <div key={i} className="border-l-2 border-[#e52620] pl-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono font-bold mb-2">
                Q {i + 1}
              </div>
              <h4 className="text-white font-bold text-lg tracking-tight">
                {d.q}
              </h4>
              <p className="mt-2 text-zinc-400 leading-relaxed">{d.a}</p>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export default function Spectrum() {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(null);

  return (
    <section
      id="spectrum"
      data-testid="spectrum-section"
      className="relative py-16 md:py-20 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[#e52620]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono">
              {t.spectrum.eyebrow}
            </span>
          </div>
          <h2
            data-testid="spectrum-title"
            className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
          >
            {t.spectrum.title}
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            {t.spectrum.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {t.spectrum.items.map((item, i) => (
            <SpectrumCard
              key={item.key}
              item={item}
              index={i}
              expanded={expanded === item.key}
              onToggle={() => setExpanded(expanded === item.key ? null : item.key)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
